import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  TextField,
} from '@mui/material';
import {
  RateReview as ReviewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Block as RevokeIcon,
  Close as CloseIcon,
  ToggleOn as ToggleIcon,
} from '@mui/icons-material';
import { formatDateWithCulture } from '../../../utils/dateUtils';

function ReviewDeviceModal({
  open,
  onClose,
  deviceData = null,
  deviceStatuses = [],
  deviceTypes = [],
  onApprove,
  onReject,
  onRevoke,
  onToggleLGSPAccess,
  i18n,
}) {
  const { t } = useTranslation();
  const [confirmAction, setConfirmAction] = useState(null); // 'reject' or 'revoke'
  const [statusNote, setStatusNote] = useState('');

  if (!deviceData) return null;

  const statusObj = deviceStatuses.find((status) => status.id === deviceData.status);
  const statusName = statusObj ? statusObj.name : '-';

  // Get device type name from deviceTypes array
  const deviceTypeObj = deviceTypes.find((type) => type.id === deviceData.deviceType);
  const deviceTypeName = deviceTypeObj ? deviceTypeObj.name : (deviceData.deviceType ?? '-');

  const handleApprove = () => {
    onApprove(deviceData.id);
  };

  const handleReject = () => {
    setConfirmAction('reject');
    setStatusNote('');
  };

  const handleRevoke = () => {
    setConfirmAction('revoke');
    setStatusNote('');
  };

  const handleConfirmAction = () => {
    if (confirmAction === 'reject') {
      onReject(deviceData.id, statusNote);
    } else if (confirmAction === 'revoke') {
      onRevoke(deviceData.id, statusNote);
    }
    setConfirmAction(null);
    setStatusNote('');
    onClose();
  };

  const handleCancelAction = () => {
    setConfirmAction(null);
    setStatusNote('');
  };

  const handleToggleLGSPAccess = () => {
    if (onToggleLGSPAccess) {
      onToggleLGSPAccess(deviceData.id, !deviceData.allowLGSPAccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <ReviewIcon />
        {t('devices.reviewDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t('devices.reviewDialog.deviceDetails')}
          </Typography>

          <Grid container spacing={3}>
            {/* Device Code */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.deviceCode')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.deviceCode || '-'}
              </Typography>
            </Grid>

            {/* Name */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.name')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.name || '-'}
              </Typography>
            </Grid>

            {/* Unit Name */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.unitName')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.unitName || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Device Type */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.deviceType')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceTypeName}
              </Typography>
            </Grid>

            {/* Model */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.model')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.model || '-'}
              </Typography>
            </Grid>

            {/* Serial Number */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.serialNumber')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.serialNumber || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Registration Date */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.registrationDate')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDateWithCulture(deviceData.registrationDate, i18n.language) || '-'}
              </Typography>
            </Grid>

            {/* Approved Date */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.approvedDate')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDateWithCulture(deviceData.approvedDate, i18n.language) || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Status */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.status')}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  backgroundColor: statusObj ? '#e3f2fd' : '#e2e8f0',
                  color: statusObj ? '#1565c0' : '#64748b',
                }}
              >
                {statusName}
              </Box>
            </Grid>

            {/* Is Valid */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.isValid')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.isValid ? t('devices.valid.yes') : t('devices.valid.no')}
              </Typography>
            </Grid>

            {/* Allow LGSP Access */}
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.table.allowLGSPAccess')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {deviceData.allowLGSPAccess ? t('devices.lgspAccess.yes') : t('devices.lgspAccess.no')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
        {confirmAction ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t(`devices.reviewDialog.${confirmAction}ReasonPrompt`)}
            </Typography>
            <TextField
              label={t('devices.reviewDialog.statusNote')}
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              multiline
              rows={3}
              fullWidth
              required
              inputProps={{
                id: 'statusNote',
                name: 'statusNote',
                'data-testid': 'review-modal-input-statusNote',
                maxLength: 500,
              }}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCancelAction}
                variant="outlined"
                id="cancelAction"
                name="cancelAction"
                data-testid="review-modal-button-cancelAction"
              >
                {t('devices.reviewDialog.cancel')}
              </Button>
              <Button
                onClick={handleConfirmAction}
                variant="contained"
                color="error"
                disabled={!statusNote.trim()}
                id="confirmAction"
                name="confirmAction"
                data-testid="review-modal-button-confirmAction"
              >
                {t(`devices.reviewDialog.confirm${confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}`)}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleApprove}
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                id="approve"
                name="approve"
                data-testid="review-modal-button-approve"
              >
                {t('devices.reviewDialog.approve')}
              </Button>
              <Button
                onClick={handleReject}
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                id="reject"
                name="reject"
                data-testid="review-modal-button-reject"
              >
                {t('devices.reviewDialog.reject')}
              </Button>
              <Button
                onClick={handleRevoke}
                variant="contained"
                color="error"
                startIcon={<RevokeIcon />}
                id="revoke"
                name="revoke"
                data-testid="review-modal-button-revoke"
              >
                {t('devices.reviewDialog.revoke')}
              </Button>
              <Button
                onClick={handleToggleLGSPAccess}
                variant="contained"
                color="warning"
                startIcon={<ToggleIcon />}
                id="toggleLGSPAccess"
                name="toggleLGSPAccess"
                data-testid="review-modal-button-toggleLGSPAccess"
              >
                {deviceData?.allowLGSPAccess
                  ? t('devices.reviewDialog.disableLGSPAccess')
                  : t('devices.reviewDialog.enableLGSPAccess')
                }
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={onClose}
                variant="outlined"
                startIcon={<CloseIcon />}
                id="exit"
                name="exit"
                data-testid="review-modal-button-exit"
              >
                {t('devices.reviewDialog.exit')}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ReviewDeviceModal;
